import type { ReactNode } from 'react'
import './MarkdownLite.css'

// A tiny, dependency-free markdown renderer for short agent-authored text (e.g. the guidance-update
// analysis). It is deliberately NOT a full markdown implementation — it handles the block + inline
// constructs agents actually emit (headings, ordered/unordered lists, paragraphs, **bold**, `code`,
// [text](url) links, bare URLs, and ![alt](url) images) and renders everything through real React nodes,
// so there is no dangerouslySetInnerHTML and no HTML injection surface. Anything it doesn't recognize is
// shown verbatim as paragraph text.

// Only http(s), mailto, and root-relative app targets are turned into anchors. Root-relative links are
// supported for older persisted notices; newly generated Test Run reports use full Replay QA URLs so
// the same Markdown remains clickable when copied into GitHub. Protocol-relative and script URLs stay
// inert.
function isSafeUrl(url: string): boolean {
  return /^https?:\/\//i.test(url) || /^mailto:/i.test(url) || /^\/(?!\/)/.test(url)
}

// Markdown escaping is necessary when the report is posted to GitHub, but the renderer should display
// the intended label rather than the source backslashes. This mirrors CommonMark's escapable ASCII
// punctuation without unescaping arbitrary sequences.
function unescapeMarkdown(value: string): string {
  return value.replace(/\\([\\`*_[\]{}()#+.!|>~-])/g, '$1')
}

// Trailing punctuation that commonly follows a bare URL in prose but isn't part of it, e.g. the
// period in "see https://example.com." or the paren in "(https://example.com)".
const URL_TRAILING_PUNCT = /[.,:;!?'"\)\]}]+$/

function link(url: string, label: string, key: number): ReactNode {
  const external = /^https?:\/\//i.test(url)
  return (
    <a
      key={key}
      className="markdown-lite__link"
      href={url}
      target={external ? '_blank' : undefined}
      rel={external ? 'noopener noreferrer' : undefined}
    >
      {label}
    </a>
  )
}

// Only remote images are rendered. A round's summary embeds the screenshots it attached as
// `![caption](https://…)` and that same Markdown goes to GitHub, so the page has to show what the PR
// comment shows; anything else (a relative path, a data: URI) stays verbatim text.
function image(url: string, alt: string, key: number): ReactNode {
  return <img key={key} className="markdown-lite__image" src={url} alt={alt} loading="lazy" />
}

// Inline: split a line into text / ![alt](url) image / **bold** / `code` / [text](url) link / bare-URL
// nodes. The link label branch treats a backslash plus the following character as one unit, so
// GitHub-safe labels such as `[Checkout \[crashes\]](https://…)` do not end at the escaped bracket.
function renderInline(text: string): ReactNode[] {
  const nodes: ReactNode[] = []
  // Order matters: ![alt](url) before [text](url) (or the image renders as a stray "!" plus a link),
  // and both before a bare URL so a target isn't matched twice.
  const regex = /!\[((?:\\.|[^\]])*)\]\(([^)\s]+)\)|\*\*([^*]+)\*\*|`([^`]+)`|\[((?:\\.|[^\]])+)\]\(([^)\s]+)\)|(https?:\/\/[^\s<]+)/g
  let lastIndex = 0
  let key = 0
  let m: RegExpExecArray | null
  while ((m = regex.exec(text)) !== null) {
    if (m.index > lastIndex) nodes.push(unescapeMarkdown(text.slice(lastIndex, m.index)))
    if (m[1] !== undefined && m[2] !== undefined) {
      // ![alt](url) — render remote images; show anything else as its source.
      nodes.push(/^https?:\/\//i.test(m[2]) ? image(m[2], unescapeMarkdown(m[1]), key++) : unescapeMarkdown(m[0]))
    } else if (m[3] !== undefined) {
      nodes.push(<strong key={key++}>{unescapeMarkdown(m[3])}</strong>)
    } else if (m[4] !== undefined) {
      nodes.push(<code key={key++} className="markdown-lite__code">{m[4]}</code>)
    } else if (m[5] !== undefined && m[6] !== undefined) {
      // [text](url) — linkify only safe targets; otherwise show the source verbatim.
      nodes.push(isSafeUrl(m[6]) ? link(m[6], unescapeMarkdown(m[5]), key++) : unescapeMarkdown(m[0]))
    } else if (m[7] !== undefined) {
      // Bare URL — strip trailing sentence punctuation so it isn't swallowed into the href.
      const punct = URL_TRAILING_PUNCT.exec(m[7])
      const url = punct ? m[7].slice(0, m[7].length - punct[0].length) : m[7]
      nodes.push(link(url, url, key++))
      if (punct) nodes.push(m[7].slice(url.length))
    }
    lastIndex = regex.lastIndex
  }
  if (lastIndex < text.length) nodes.push(unescapeMarkdown(text.slice(lastIndex)))
  return nodes
}

const HEADING_RE = /^(#{1,6})\s+(.*)$/
const ULIST_RE = /^[-*]\s+(.*)$/
const OLIST_RE = /^\d+\.\s+(.*)$/
const FENCE_RE = /^```/
const HR_RE = /^(-{3,}|\*{3,}|_{3,})$/
const isBlockStart = (line: string) =>
  HEADING_RE.test(line) || ULIST_RE.test(line) || OLIST_RE.test(line) || FENCE_RE.test(line) || HR_RE.test(line)

// --- GFM pipe tables ---------------------------------------------------------------------------
// Agents frequently answer with a Markdown table (e.g. "who are the project members?"). Without table
// support the header, the `| --- | --- |` delimiter, and every row collapse into one run-on paragraph
// of raw pipes — the raw-markdown bug this renderer is meant to avoid. We parse the standard GFM pipe
// table (optional outer pipes, per-column `:` alignment) and render it as a real <table>.
type Align = 'left' | 'right' | 'center' | undefined

// Split one table row into cells on unescaped `|`, dropping the optional leading/trailing pipe. A
// backslash-escaped `\|` stays inside its cell (renderInline's unescape turns it back into a literal
// pipe), so a cell can contain a pipe without breaking the columns.
function splitRow(line: string): string[] {
  let s = line.trim()
  if (s.startsWith('|')) s = s.slice(1)
  if (s.endsWith('|') && !s.endsWith('\\|')) s = s.slice(0, -1)
  const cells: string[] = []
  let cur = ''
  for (let k = 0; k < s.length; k++) {
    const ch = s[k]!
    if (ch === '\\' && k + 1 < s.length) { cur += ch + s[k + 1]!; k++; continue }
    if (ch === '|') { cells.push(cur.trim()); cur = ''; continue }
    cur += ch
  }
  cells.push(cur.trim())
  return cells
}

const DELIM_CELL_RE = /^:?-+:?$/
// A delimiter row is the `--- | :--: | ---:` line under the header: every cell is dashes with optional
// alignment colons. It's what distinguishes a real table from a paragraph that merely contains a pipe.
function isDelimiterRow(line: string): boolean {
  const cells = splitRow(line)
  return cells.length > 0 && cells.every((c) => DELIM_CELL_RE.test(c))
}

function cellAlign(cell: string): Align {
  const left = cell.startsWith(':')
  const right = cell.endsWith(':')
  if (left && right) return 'center'
  if (right) return 'right'
  if (left) return 'left'
  return undefined
}

// A table starts where a header row is immediately followed by a delimiter row with a matching column
// count. Requiring the pair (not just a stray pipe) keeps ordinary prose containing a `|` as a paragraph.
function isTableStart(lines: string[], i: number): boolean {
  if (i + 1 >= lines.length) return false
  const header = lines[i]!.trim()
  const delim = lines[i + 1]!.trim()
  if (!header.includes('|') || !isDelimiterRow(delim)) return false
  return splitRow(header).length === splitRow(delim).length
}

function consumeTable(
  lines: string[],
  start: number,
): { header: string[]; aligns: Align[]; rows: string[][]; next: number } {
  const header = splitRow(lines[start]!)
  const aligns = splitRow(lines[start + 1]!).map(cellAlign)
  const rows: string[][] = []
  let i = start + 2
  while (i < lines.length) {
    const cur = lines[i]!.trim()
    if (cur === '' || isBlockStart(cur)) break
    rows.push(splitRow(lines[i]!))
    i++
  }
  return { header, aligns, rows, next: i }
}

// Consume consecutive list items matching `re`, starting at `start`. When `loose` is set, a blank
// line between items is treated as part of the same list (standard "loose list" behaviour) instead
// of ending it — otherwise an agent that separates numbered items with blank lines produces a
// separate <ol> per item, each restarting at "1.". Returns the collected item texts + the next index.
function consumeList(
  lines: string[],
  start: number,
  re: RegExp,
  loose: boolean,
): { items: string[]; next: number } {
  const items: string[] = []
  let i = start
  while (i < lines.length) {
    const cur = lines[i]!.trim()
    if (re.test(cur)) {
      items.push(re.exec(cur)![1]!)
      i++
      continue
    }
    if (loose && cur === '') {
      // Peek past blank line(s): stay in the list only if another item of the same kind follows.
      let j = i + 1
      while (j < lines.length && lines[j]!.trim() === '') j++
      if (j < lines.length && re.test(lines[j]!.trim())) { i = j; continue }
    }
    break
  }
  return { items, next: i }
}

export default function MarkdownLite({ text, className, looseLists = false }: { text: string; className?: string; looseLists?: boolean }) {
  const lines = text.replace(/\r\n/g, '\n').split('\n')
  const blocks: ReactNode[] = []
  let i = 0
  let key = 0

  while (i < lines.length) {
    const trimmed = lines[i]!.trim()

    // Blank line — skip; it just separates blocks.
    if (trimmed === '') { i++; continue }

    // Fenced code block — render verbatim. Also gracefully swallows stray ``` fences an agent may
    // wrap the whole analysis in.
    if (FENCE_RE.test(trimmed)) {
      i++ // skip opening fence
      const code: string[] = []
      while (i < lines.length && !FENCE_RE.test(lines[i]!.trim())) { code.push(lines[i]!); i++ }
      if (i < lines.length) i++ // skip closing fence
      blocks.push(<pre key={key++} className="markdown-lite__pre"><code>{code.join('\n')}</code></pre>)
      continue
    }

    // Horizontal rule
    if (HR_RE.test(trimmed)) {
      blocks.push(<hr key={key++} className="markdown-lite__hr" />)
      i++
      continue
    }

    // Heading (rendered as a styled subheading regardless of level to keep one visual scale).
    const heading = HEADING_RE.exec(trimmed)
    if (heading) {
      blocks.push(<p key={key++} className="markdown-lite__heading">{renderInline(heading[2] ?? '')}</p>)
      i++
      continue
    }

    // Unordered list — consume consecutive bullet lines.
    if (ULIST_RE.test(trimmed)) {
      const { items, next } = consumeList(lines, i, ULIST_RE, looseLists)
      i = next
      blocks.push(
        <ul key={key++} className="markdown-lite__list">
          {items.map((it, idx) => <li key={idx}>{renderInline(it)}</li>)}
        </ul>,
      )
      continue
    }

    // Ordered list — consume consecutive numbered lines.
    if (OLIST_RE.test(trimmed)) {
      const { items, next } = consumeList(lines, i, OLIST_RE, looseLists)
      i = next
      blocks.push(
        <ol key={key++} className="markdown-lite__list">
          {items.map((it, idx) => <li key={idx}>{renderInline(it)}</li>)}
        </ol>,
      )
      continue
    }

    // Table (GFM pipe table) — a header row followed by a `--- | ---` delimiter row.
    if (isTableStart(lines, i)) {
      const { header, aligns, rows, next } = consumeTable(lines, i)
      i = next
      const styleFor = (idx: number) => {
        const a = aligns[idx]
        return a ? { textAlign: a } : undefined
      }
      blocks.push(
        <table key={key++} className="markdown-lite__table">
          <thead>
            <tr>
              {header.map((cell, idx) => (
                <th key={idx} style={styleFor(idx)}>{renderInline(cell)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, ri) => (
              <tr key={ri}>
                {header.map((_, ci) => (
                  <td key={ci} style={styleFor(ci)}>{renderInline(row[ci] ?? '')}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>,
      )
      continue
    }

    // Paragraph — consume consecutive plain lines until a blank line or a new block starts.
    // Soft-wrapped lines (a single newline mid-paragraph, as agents emit when hard-wrapping at a
    // column width) join with a space and reflow, matching standard markdown — inserting a <br />
    // at every wrap point produced ugly, unexpected line breaks in the rendered text.
    const para: string[] = []
    while (i < lines.length && lines[i]!.trim() !== '' && !isBlockStart(lines[i]!.trim()) && !isTableStart(lines, i)) {
      para.push(lines[i]!.trim())
      i++
    }
    blocks.push(
      <p key={key++} className="markdown-lite__paragraph">
        {renderInline(para.join(' '))}
      </p>,
    )
  }

  return <div className={className ? `markdown-lite ${className}` : 'markdown-lite'}>{blocks}</div>
}
